/* eslint-disable react/jsx-props-no-spreading */
import styled from '@emotion/styled';
import {spacing} from '@nfq/react-grid';
import {getPayload} from 'payload';

import {SeoHead} from 'UI/components/layout/SeoHead';
import {HeroSection} from 'UI/components/modules/HeroSection';

import payloadConfig from '@payload-config';
import {DEFAULT_LOCALE} from 'GlobalShared/locales';
import {buildResponsiveImageData} from 'Shared/buildResponsiveImageData';
import {AppLayout} from 'UI/layouts/AppLayout';
import {BlockRenderer} from 'UI/modules/BlockRenderer';

import {CONTENT_POPULATE} from 'Payload/utils/contentPopulate';
import {getLegalPages} from 'Payload/utils/getLegalPages';
import {buildWebPageJsonLd} from 'UI/utils/jsonLd';

import type {GetStaticPaths, GetStaticProps, InferGetStaticPropsType} from 'next';
import type {Payload} from 'payload';
import type {NextSSGPageWithLayout} from 'types/global';
import type {AudioFile, Config, Landingpage, LegalPage} from 'types/payload-types';

const REVALIDATE_SECONDS = 18000;
const HEADER_IMAGE_WIDTH = 1200;
// Two levels are required for Walk -> Waypoint map markers. The populate select below
// keeps that depth from exposing unrelated fields of either document.
const RELATIONSHIP_DEPTH = 2;

interface LandingpageProps {
    landingpage: Landingpage;
    legalPages: LegalPage[];
}

type UnknownRecord = Record<string, unknown>;
const RESPONSIVE_IMAGE_SIZE_KEYS = ['thumbnail', 'card', 'tablet', 'hero', 'heroLarge'];

/**
 * Checks whether a value is a non-null object record.
 *
 * @param value The value to check.
 * @returns `true` when the value can be accessed as a record.
 */
const isRecord = (value: unknown): value is UnknownRecord => (
    typeof value === 'object' && value !== null
);

/**
 * Keeps only the public image fields required by `buildResponsiveImageData`.
 *
 * @param media A populated Media document or relation ID.
 * @returns A minimal media relation.
 */
const sanitizeMedia = (media: unknown): unknown => {
    if (typeof media === 'string' || !isRecord(media)) return media;

    const mediaSizes = isRecord(media.sizes) ? media.sizes : null;
    const sizes = mediaSizes
        ? Object.fromEntries(RESPONSIVE_IMAGE_SIZE_KEYS.map(key => {
            const size = mediaSizes[key];

            return [key, isRecord(size) ? {
                url: size.url,
                width: size.width
            } : size];
        }))
        : media.sizes;

    return {
        alt: media.alt,
        focalX: media.focalX,
        focalY: media.focalY,
        sizes,
        url: media.url,
        width: media.width
    };
};

/**
 * Keeps the fields needed to render a public walk card and, optionally, its map markers.
 *
 * @param walk             A populated Walk document or relation ID.
 * @param includeWaypoints Whether map waypoint data should be retained.
 * @returns A minimal walk relation.
 */
const sanitizeWalk = (walk: unknown, includeWaypoints: boolean): unknown => {
    if (typeof walk === 'string' || !isRecord(walk)) return walk;

    const result: UnknownRecord = {
        categories: Array.isArray(walk.categories)
            ? walk.categories.map(category => (isRecord(category)
                ? {
                    id: category.id,
                    name: category.name
                }
                : category))
            : walk.categories,
        city: isRecord(walk.city) ? {
            id: walk.city.id,
            name: walk.city.name
        } : walk.city,
        coverImage: sanitizeMedia(walk.coverImage),
        general: isRecord(walk.general)
            ? {
                distance: walk.general.distance,
                duration: walk.general.duration
            }
            : walk.general,
        id: walk.id,
        pricing: isRecord(walk.pricing)
            ? {
                isFree: walk.pricing.isFree,
                price: walk.pricing.price
            }
            : walk.pricing,
        shortDescription: walk.shortDescription,
        slug: walk.slug,
        title: walk.title
    };

    if (includeWaypoints && isRecord(walk.waypoints)) {
        result.waypoints = {
            docs: Array.isArray(walk.waypoints.docs)
                ? walk.waypoints.docs.map(waypoint => sanitizeWaypoint(waypoint, false))
                : walk.waypoints.docs
        };
    }

    return result;
};

/**
 * Keeps only public waypoint teaser and map fields.
 *
 * @param waypoint    A populated Waypoint document or relation ID.
 * @param includeWalk Whether the parent walk relation should be retained.
 * @returns A minimal waypoint relation.
 */
const sanitizeWaypoint = (waypoint: unknown, includeWalk: boolean): unknown => {
    if (typeof waypoint === 'string' || !isRecord(waypoint)) return waypoint;

    return {
        id: waypoint.id,
        images: Array.isArray(waypoint.images)
            ? waypoint.images.map(image => (isRecord(image)
                ? {
                    id: image.id,
                    image: sanitizeMedia(image.image)
                }
                : image))
            : waypoint.images,
        location: waypoint.location,
        shortDescription: waypoint.shortDescription,
        sortOrder: waypoint.sortOrder,
        title: waypoint.title,
        walk: includeWalk ? sanitizeWalk(waypoint.walk, false) : waypoint.walk
    };
};

/**
 * Recursively strips populated landing-page relationships to their public field whitelist.
 *
 * @param value A landing-page document or nested block value.
 * @param key   The current property name.
 * @returns The sanitized value.
 */
const sanitizeLandingpageValue = (value: unknown, key?: string): unknown => {
    if (key === 'walk') return sanitizeWalk(value, true);
    if (key === 'walks' && Array.isArray(value)) return value.map(walk => sanitizeWalk(walk, true));
    if (key === 'waypoint') return sanitizeWaypoint(value, true);
    if (key === 'waypoints' && Array.isArray(value)) {
        return value.map(waypoint => sanitizeWaypoint(waypoint, true));
    }
    if (['coverImage', 'headerImage', 'image', 'profileImage'].includes(key ?? '')) {
        return sanitizeMedia(value);
    }
    if (key === 'audioFile' && isRecord(value)) {
        return {
            duration: value.duration,
            filename: value.filename,
            id: value.id,
            mimeType: value.mimeType,
            title: value.title,
            url: value.url
        };
    }
    if (Array.isArray(value)) return value.map(item => sanitizeLandingpageValue(item));
    if (!isRecord(value)) return value;

    return Object.fromEntries(Object.entries(value).map(([entryKey, entryValue]) => [
        entryKey,
        sanitizeLandingpageValue(entryValue, entryKey)
    ]));
};

/**
 * Sanitizes a populated landing page before it crosses the server-to-client boundary.
 *
 * @param landingpage The populated landing page from Payload.
 * @returns The same landing page shape with only public relationship fields.
 */
const sanitizeLandingpage = (landingpage: Landingpage): Landingpage => (
    JSON.parse(JSON.stringify(sanitizeLandingpageValue(landingpage))) as Landingpage
);

/**
 * Resolves a playable audio URL from Payload's direct URL or upload filename.
 *
 * @param audioFile The populated landingpage audio file.
 * @returns A playable URL or null when no upload reference exists.
 */
const getLandingpageAudioUrl = (audioFile: AudioFile): string | null => (
    audioFile.url
    ?? (audioFile.filename
        ? `/api/audio-files/file/${encodeURIComponent(audioFile.filename)}`
        : null)
);

/**
 * Frei gestaltbare Landingpage aus den gemeinsamen Inhaltsblöcken (`PAGE_BUILDER_BLOCKS`).
 *
 * @param props             Die Seiten-Props.
 * @param props.landingpage Das Landingpage-Dokument mit Block-Konfiguration.
 * @returns ReactNode der Landingpage.
 */
const LandingpageView: NextSSGPageWithLayout<typeof getStaticProps> = (
    {landingpage}: InferGetStaticPropsType<typeof getStaticProps>
) => (
    <Page data-cy="LandingpageView">
        <SeoHead
            description={landingpage.meta?.description}
            jsonLd={[
                buildWebPageJsonLd(
                    {
                        description: landingpage.meta?.description ?? undefined,
                        name: landingpage.meta?.title ?? landingpage.title,
                        url: `${process.env.NEXT_PUBLIC_BASE_URL}/${landingpage.slug}`
                    },
                    [
                        {
                            name: 'Home',
                            url: `${process.env.NEXT_PUBLIC_BASE_URL}/`
                        },
                        {
                            name: landingpage.title,
                            url: `${process.env.NEXT_PUBLIC_BASE_URL}/${landingpage.slug}`
                        }
                    ]
                )
            ]}
            title={landingpage.meta?.title ?? landingpage.title}
            url={`${process.env.NEXT_PUBLIC_BASE_URL}/${landingpage.slug}`}
        />
        <HeroSection
            imageAlt={landingpage.headerImage && typeof landingpage.headerImage === 'object'
                ? landingpage.headerImage.alt ?? landingpage.title
                : landingpage.title}
            imageFocalX={landingpage.headerImage && typeof landingpage.headerImage === 'object'
                ? landingpage.headerImage.focalX
                : undefined}
            imageFocalY={landingpage.headerImage && typeof landingpage.headerImage === 'object'
                ? landingpage.headerImage.focalY
                : undefined}
            imageSrcSet={landingpage.headerImage && typeof landingpage.headerImage === 'object'
                ? buildResponsiveImageData(landingpage.headerImage, HEADER_IMAGE_WIDTH)?.srcSet
                : undefined}
            imageUrl={landingpage.headerImage && typeof landingpage.headerImage === 'object'
                ? buildResponsiveImageData(landingpage.headerImage, HEADER_IMAGE_WIDTH)?.src
                : undefined}
            testId="LandingpageHero"
            title={landingpage.title}
        />
        {landingpage.audioFile && typeof landingpage.audioFile === 'object'
            && getLandingpageAudioUrl(landingpage.audioFile) && (
                <AudioFigure>
                    <AudioPlayer
                        preload="metadata"
                        src={getLandingpageAudioUrl(landingpage.audioFile) ?? undefined}
                        controls
                    />
                </AudioFigure>
        )}
        <BlockRenderer blocks={landingpage.blocks as Parameters<typeof BlockRenderer>[0]['blocks']} />
    </Page>
);

LandingpageView.displayName = 'LandingpageView';

/**
 * `getLayout` wraps die Seite mit dem App-Shell-Layout.
 *
 * @param _route        Die aktuelle Route.
 * @param pageProps     Die Seiten-Props.
 * @param PageComponent Die Seitenkomponente.
 * @returns ReactNode mit AppLayout.
 */
LandingpageView.getLayout = (_route, pageProps, PageComponent) => (
    <AppLayout activeTab="landingpage" legalPages={pageProps.legalPages} isBackVisible realBackLink>
        <PageComponent {...pageProps} />
    </AppLayout>
);

/**
 * `getLayoutKey` gibt den Layout-Schlüssel zurück.
 *
 * @returns Der Layout-Key-String.
 */
LandingpageView.getLayoutKey = () => 'landingpage';

export default LandingpageView;

// ── Static Generation ───────────────────────────────────────

/**
 * Lädt alle Landingpage-Slugs für die statische Generierung.
 *
 * @returns Pfade und Fallback-Konfiguration.
 */
export const getStaticPaths: GetStaticPaths = () => ({
    fallback: 'blocking',
    paths: []
});

/**
 * Lädt das Landingpage-Dokument anhand des Slugs. Next.js priorisiert literale Routen
 * (`/walks`, `/legal`, `/blog`, `/login`, …) automatisch vor diesem Catch-all, daher gibt
 * es keine Kollision mit bestehenden Seiten.
 *
 * @param context                  Der Next.js Static-Props Kontext.
 * @param context.locale           Die aktuelle Locale.
 * @param context.params           Die Route-Parameter mit Slug.
 * @param context.revalidateReason Der Grund für die Revalidierung.
 * @returns Props mit dem Landingpage-Dokument oder notFound.
 */
export const getStaticProps: GetStaticProps<LandingpageProps> = async ({locale, params, revalidateReason}) => {
    const slug = params?.slug as string;
    let payload: Payload | undefined;

    if (revalidateReason !== 'build') {
        payload = await getPayload({config: payloadConfig});
    }

    if (!payload) {
        return {notFound: true};
    }

    const payloadLocale = (locale ?? DEFAULT_LOCALE) as Config['locale'];

    const [result, legalPages] = await Promise.all([
        payload.find({
            collection: 'landingpages',
            depth: RELATIONSHIP_DEPTH,
            locale: payloadLocale,
            overrideAccess: true,
            populate: CONTENT_POPULATE,
            where: {
                and: [
                    {slug: {equals: slug}},
                    {_status: {equals: 'published'}}
                ]
            }
        }),
        getLegalPages(payload, payloadLocale)
    ]);

    const landingpage = result.docs[0] as Landingpage | undefined;

    if (!landingpage) {
        return {notFound: true};
    }

    if (typeof landingpage.audioFile === 'string') {
        const audioFile = await payload.findByID({
            collection: 'audio-files',
            depth: 0,
            id: landingpage.audioFile,
            overrideAccess: true,
            select: {
                duration: true,
                filename: true,
                mimeType: true,
                title: true,
                url: true
            }
        });

        landingpage.audioFile = audioFile as AudioFile;
    }

    return {
        props: {
            landingpage: sanitizeLandingpage(
                JSON.parse(JSON.stringify(landingpage)) as Landingpage
            ),
            legalPages
        },
        revalidate: REVALIDATE_SECONDS
    };
};

// ── Styled ──────────────────────────────────────────────────

const Page = styled.div`
    background: ${({theme}) => theme.colors.surfaceBackground};
    display: flex;
    flex-direction: column;
    gap: ${spacing(8)};
    min-height: 100dvh;
    padding: ${spacing(6)} ${spacing(6)} ${spacing(10)};
`;

const AudioFigure = styled.figure`
    background: ${({theme}) => theme.colors.surfaceLow};
    border-radius: ${spacing(3)};
    bottom: ${spacing(8)};
    box-shadow: ${({theme}) => theme.boxShadows.floatingShadow};
    display: flex;
    flex-direction: column;
    gap: ${spacing(2)};
    margin: 0;
    max-width: 72rem;
    padding: ${spacing(4)};
    position: sticky;
    z-index: 10;
`;

const AudioPlayer = styled.audio`
    width: 100%;
`;